<template>
  <div
    v-if="hasValidFileId"
    class="assignment-card"
    :class="{
      selected: selectedAssignment && selectedAssignment.id === assignment.id,
      'from-email': isFromEmail,
    }"
    @click="handleCardClick"
    style="position: relative"
  >
    <div class="assignment-title mb-1">
      <div class="property-name-row">
        <p class="assignment-client ma-0 pa-0">
          {{ getFormattedPropertyAddressOrName(assignment, true) }}
        </p>
        <div class="status-label" v-if="showActiveLabel">
          {{ getAssignmentStatus(assignment) }}
        </div>
        <div class="icons-container">
          <div @click.stop="openBoxFolder(assignment)" class="icon-button">
            <img :src="require('../../../../assets/box.png')" width="18" height="20" />
          </div>
          <div @click.stop="openDocument(assignment)" class="icon-button">
            <IconProvider name="FileText" class="icon cbre-green" />
          </div>
        </div>
      </div>
      <!-- <p class="assignment-client ma-0 pa-0"><span class="pr-3">{{ getFormattedPropertyAddress(assignment) }}</span><v-spacer></v-spacer><span>{{ assignment.trackerEngagementStatus.name }}</span></p> -->
      <p class="assignment-address ma-0 pa-0">
        {{ getClientName(assignment.client) }} |
        {{ getFormattedPropertyAddressOrName(assignment, false) }}
      </p>
    </div>
    <v-row class="assignment-detail mt-1 pb-2">
      <v-col cols="6" class="pa-0">
        <LabelValue
          label="Primary Valuer"
          :value="
            assignment.primaryAppraiser ||
            assignment.primaryAppraiserName ||
            assignment.primaryAppraiser?.name ||
            assignment.primaryAppraiser?.fullName
          "
        />
      </v-col>
      <v-col cols="6" class="pa-0">
        <LabelValue
          label="Originator"
          :value="
            assignment.originator ||
            assignment.originatorName ||
            assignment.originator?.name ||
            assignment.jobOriginator?.fullName
          "
        />
      </v-col>
      <v-col cols="6" class="pa-0">
        <LabelValue
          label="File ID"
          :value="assignment.code || assignment.userFriendlyId || assignment.fileId"
          :valueClass="'hyperlink'"
          :linkConfig="getLinkConfig(assignment)"
          @click.native.stop="onFileIdClick"
        />
      </v-col>

      <v-col cols="6" class="pa-0">
        <LabelValue
          label="Property Type"
          :value="getPropertyTypeName(assignment.propertyType)"
        />
      </v-col>
      <v-col cols="6" class="pa-0">
        <LabelValue
          label="Assignment Due Date"
          :value="
            formatDate(
              assignment.deadline?.assignmentDueDate || assignment.assignmentDueDate
            )
          "
        />
      </v-col>
      <v-col cols="6" class="pa-0">
        <LabelValue
          label="Created Date"
          :value="
            formatCreatedDateOnly(
              assignment.createdTimestamp ||
                assignment.createdDate ||
                assignment.dateCreated ||
                assignment.created
            ) || 'No date available'
          "
        />
      </v-col>
    </v-row>

    <!-- Sandboxed Loading Component -->
    <div v-if="showLoading" class="sandboxed-loader-overlay">
      <div class="loader-container">
        <div class="loading-spinner"></div>
      </div>
    </div>
  </div>
</template>

<script>
import LabelValue from "../../commons/LabelValue.vue";
import { getPropertyAddressOrName, getWorkfileId } from "@/utils/globalUtil.js";
import IconProvider from "../../commons/IconProvider.vue";

export default {
  name: "AssignmentCard",
  props: {
    assignment: Object,
    selectedAssignment: Object,
    showActiveLabel: {
      type: Boolean,
      default: false,
    },
    enableDialogTrigger: {
      type: Boolean,
      default: false,
    },
    isFromEmail: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    LabelValue,
    IconProvider,
  },
  data() {
    return {
      showLoading: false,
    };
  },
  computed: {
    hasValidFileId() {
      // Check objectType first - only show "Assignment" type
      const objectType = this.assignment?.objectType;
      return objectType === "Assignment";
    },
  },
  watch: {
    selectedAssignment: {
      immediate: true,
      handler(newVal) {},
    },
  },
  beforeDestroy() {
    // Ensure loading state is cleaned up
    this.showLoading = false;
  },
  methods: {
    getAssignmentStatus(assignment) {
      // Determine status based on assignment source
      // If from active assignments API -> "Active"
      // If from past engagements API -> "InActive"
      return assignment.assignmentSource === "active" ? "Active" : "InActive";
    },
    async openDocument(assignment) {
      try {
        this.showLoading = true;
        this.$emit("show-loader", true);

        if (!assignment) {
          return;
        }

        console.log("Assignment object:", assignment);
        console.log("WorkFileLink:", assignment.workFileLink);

        const engagementId = assignment.sourceSystemId;
        const workfileId = assignment.workFileLink.split("/")[4];
        console.log("workfileId", workfileId);

        const signedEngagementLetterObj = await this.$store.dispatch(
          "assignments/fetchEngagementOverviewDocument",
          { engagementId, workfileId }
        );
        window.open(signedEngagementLetterObj.boxFileUrl, "_blank");
      } catch (err) {
        console.warn("Failed handling signed engagement letter download:", err);
      } finally {
        this.showLoading = false;
        this.$emit("show-loader", false);
      }
    },
    getFormattedPropertyAddressOrName(assignment, needPropertyName) {
      return getPropertyAddressOrName(assignment, needPropertyName);
    },
    getLinkConfig(assignment) {
      const assignmentOverviewBaseUrl = process.env.VITE_ASSIGNMENT_OVERVIEW_URL;
      // Use userFriendlyId first, then fileId, then fallback to code
      const displayFileId =
        assignment.userFriendlyId || assignment.fileId || assignment.code;
      return {
        value: displayFileId,
        url: `${assignmentOverviewBaseUrl}${assignment.id}`,
        target: "_blank",
      };
    },
    handleCardClick() {
      // Emit selection event when the card itself is clicked
      this.$emit("select", this.assignment);
    },
    openBoxFolder(assignment) {
      if (assignment && assignment.workFileLink) {
        window.open(assignment.workFileLink, "_blank");
      }
    },
    getFormattedPropertyAddressOrName(assignment, needPropertyName) {
      return getPropertyAddressOrName(assignment, needPropertyName);
    },
    handleCardClick(e) {
      // Always emit selection so lists/dialogs can update highlighted item
      this.$emit("select", this.assignment);
      // Additionally emit a dialog trigger event only when enabled
      if (this.enableDialogTrigger) {
        this.$emit("open-dialog", this.assignment);
      }
    },
    onFileIdClick(e) {
      // Prevent card click from firing when File ID is clicked
      // (handled by .stop, but method is here for clarity/extension)
    },
    getPropertyName() {
      return this.assignment?.propertyAddress?.propertyName;
    },
    formatDate(dateStr) {
      if (!dateStr) {
        return "N/A";
      }
      try {
        // Handle different date formats
        let date;
        if (typeof dateStr === "string") {
          // Extract just the date part before the 'T' if ISO format (YYYY-MM-DD)
          if (dateStr.includes("T")) {
            const datePart = dateStr.split("T")[0];
            const [yyyy, mm, dd] = datePart.split("-");
            return `${mm.padStart(2, "0")}/${dd.padStart(2, "0")}/${yyyy}`;
          } else {
            // Try to parse as regular date
            date = new Date(dateStr);
          }
        } else {
          date = new Date(dateStr);
        }

        if (date && !isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
        }

        return "N/A";
      } catch (error) {
        console.warn("Error formatting date:", error);
        return "N/A";
      }
    },
    // Clean, single implementation retained below (duplicate above removed during cleanup)
    formatCreatedDateOnly(dateStr) {
      if (!dateStr) {
        return "N/A";
      }

      try {
        // Apply same logic as DateFormatter.formatCreatedDate (useUTCTime = false)
        const date = new Date(dateStr);

        // Use date as-is (like Tracker's created dates) - no timezone conversion
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
      } catch (error) {
        console.warn("Error formatting created date:", dateStr, error);
        return "N/A";
      }
    },

    /**
     * Get client name from client object or string
     */
    getClientName(client) {
      if (!client) {
        return "N/A";
      }

      // If it's already a string, return as is
      if (typeof client === "string") {
        return client;
      }

      // If it's an object, try to extract the name
      if (typeof client === "object") {
        // Try common client name properties
        return (
          client.clientName ||
          client.fullName ||
          client.name ||
          client.companyName ||
          client.clientCompany ||
          "Unknown Client"
        );
      }

      return client.toString() || "N/A";
    },

    /**
     * Get property type name from property type object or string
     */
    getPropertyTypeName(propertyType) {
      if (!propertyType) {
        return "N/A";
      }

      // If it's an object with name property, return the name
      if (typeof propertyType === "object" && propertyType.name) {
        return propertyType.name;
      }

      // If it's already a string, return as is
      if (typeof propertyType === "string") {
        return propertyType;
      }

      // If it's an object but no name property, try to stringify or return N/A
      return propertyType.toString() || "N/A";
    },
  },
};
</script>

<style scoped>
.selected {
  outline: 2px solid #003f2d;
  box-shadow: 0px 1px 4px 0px #003f18ba !important;
}

.assignment-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 0.75rem;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
  margin-bottom: 8px;
  box-shadow: 0px 1px 4px 0px #0000001f;
  box-shadow: 0px 1px 4px 0px #00000040;
}

/* Email-extracted assignment highlighting */
.assignment-card.from-email {
  background-color: #003f2d1a !important;
}

.cbre-green {
  color: #003f2d;
}
.icon {
  padding: 2px;
}

.property-name-row {
  display: flex !important;
  justify-content: flex-start !important;
  align-items: center !important;
  width: 100% !important;
  flex-direction: row !important;
  gap: 8px !important;
}

.icons-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.icon-button {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.icon-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.status-label {
  color: #50af78;
  font-family: Calibre-R;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  text-transform: none;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.assignment-address {
  font-family: Calibre-R;
  font-weight: 400;
  font-style: Regular;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0px;
  color: #000;
}

.assignment-client {
  display: inline-block;
  font-family: Calibre-R-Medium;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: -1.5%;
  flex: none;
  margin-right: 0;
  min-width: 0;
  max-width: calc(80% - 80px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assignment-detail {
  font-family: Calibre-R;
  font-weight: 400;
  font-style: Regular;
  font-size: 11px;
  line-height: 16px;
  letter-spacing: 0px;
}

/* General responsive styles for small screens */
@media (max-width: 480px) {
  .icons-container {
    flex-shrink: 0 !important;
    min-width: 56px !important;
  }

  .assignment-client {
    max-width: calc(80% - 65px) !important;
  }
}

/* Responsive improvements for 320px width */
@media (max-width: 320px) {
  .assignment-card {
    padding: 0.5rem !important;
    margin-bottom: 6px !important;
  }

  .status-label {
    font-size: 10px !important;
    line-height: 12px !important;
    padding: 2px 6px !important;
    flex-shrink: 0 !important;
  }

  .assignment-client {
    font-size: 13px !important;
    line-height: 20px !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    hyphens: auto !important;
    margin-right: 8px !important;
    flex: 1 !important;
    min-width: 0 !important;
    max-width: calc(80% - 70px) !important;
  }

  .property-name-row {
    flex-wrap: nowrap !important;
    min-height: 24px !important;
  }

  .assignment-address {
    font-size: 12px !important;
    line-height: 16px !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    hyphens: auto !important;
  }

  .assignment-detail {
    font-size: 12px !important;
    line-height: 14px !important;
    padding-left: 8px;
  }

  .assignment-detail .v-col {
    padding: 0 2px !important;
  }

  /* Ensure text doesn't overflow */
  .assignment-title,
  .assignment-address,
  .assignment-client {
    max-width: 80% !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  /* Make LabelValue more compact */
  .assignment-detail :deep(.label-value-container) {
    padding: 1px 2px 2px 2px !important;
    flex-direction: column !important;
    align-items: flex-start !important;
  }

  .assignment-detail :deep(.label) {
    font-size: 12px !important;
    line-height: 18px !important;
    color: #666 !important;
    margin-bottom: 4px !important;
  }

  .assignment-detail :deep(.value) {
    font-size: 9px !important;
    line-height: 11px !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
  }

  /* Make date columns stack vertically at 320px */
  .assignment-detail .v-col:nth-child(5),
  .assignment-detail .v-col:nth-child(6) {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  /* Ensure icons remain visible and accessible */
  .icons-container {
    flex-shrink: 0 !important;
    min-width: 60px !important;
    margin-left: 8px !important;
  }

  .icon-button {
    padding: 2px !important;
    min-width: 24px !important;
    min-height: 24px !important;
  }

  .icon-button .icon {
    font-size: 16px !important;
    padding: 1px !important;
  }

  /* Adjust text max-width to accommodate icons */
  .assignment-client {
    max-width: calc(80% - 70px) !important;
  }
}

/* Sandboxed Loading Component Styles */
.sandboxed-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(128, 128, 128, 0.46);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 4px;
}

.loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(46, 88, 129, 0.2);
  border-top: 3px solid #2e5881;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
